import { Calendar, CheckCircle2, Circle, Mail, Send, Settings, Users } from 'lucide-react';

type ChecklistItem = {
  id: string;
  title: string;
  detail: string;
  done: boolean;
};

interface NewsletterRecipientsGateProps {
  hasInboxSubject: boolean;
  hasPostmarkTemplate: boolean;
  hasContentBlocks: boolean;
  hasSchedule: boolean;
  audienceLabel: string;
  onOpenSettings: () => void;
  onOpenContent: () => void;
  onOpenActions: () => void;
}

function ChecklistRow({ item }: { item: ChecklistItem }) {
  const Icon = item.done ? CheckCircle2 : Circle;
  return (
    <li className="flex gap-3">
      <Icon
        className={`w-5 h-5 shrink-0 mt-0.5 ${
          item.done
            ? 'text-emerald-600 dark:text-emerald-400'
            : 'text-gray-300 dark:text-gray-600'
        }`}
        aria-hidden
      />
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900 dark:text-white">{item.title}</p>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{item.detail}</p>
      </div>
    </li>
  );
}

export default function NewsletterRecipientsGate({
  hasInboxSubject,
  hasPostmarkTemplate,
  hasContentBlocks,
  hasSchedule,
  audienceLabel,
  onOpenSettings,
  onOpenContent,
  onOpenActions,
}: NewsletterRecipientsGateProps) {
  const settingsReady = hasInboxSubject && hasPostmarkTemplate;
  const contentReady = hasContentBlocks;

  const checklist: ChecklistItem[] = [
    {
      id: 'settings',
      title: 'Configure Settings',
      detail: `Set email subject in Settings (Inbox) and choose a Postmark template. Audience: ${audienceLabel}. Set status to Scheduled when committed to the send date.${
        hasSchedule ? ' A send date is set.' : ''
      }`,
      done: settingsReady,
    },
    {
      id: 'content',
      title: 'Add content blocks',
      detail:
        'At least one block is required so the email body can render when recipients are materialized.',
      done: contentReady,
    },
    {
      id: 'materialize',
      title: 'Queue recipients or set Scheduled',
      detail:
        'Set status to Scheduled in Settings when committed (auto-queue ~24h before send), or open Actions → Queue recipients / Send now to queue immediately.',
      done: false,
    },
  ];

  return (
    <div className="max-w-2xl mx-auto py-6 sm:py-10 px-2 sm:px-4 space-y-8">
      <div className="text-center space-y-2">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-900/60 mb-1">
          <Users className="w-6 h-6 text-gray-500 dark:text-gray-400" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          Recipients not materialized yet
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          This tab lists each subscriber scheduled for the send. Complete the steps below, then use{' '}
          <span className="font-medium text-gray-800 dark:text-gray-200">Actions</span> to create
          the recipient list.
        </p>
      </div>

      <ul className="space-y-5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/30 p-5 sm:p-6">
        {checklist.map((item) => (
          <ChecklistRow key={item.id} item={item} />
        ))}
      </ul>

      <div className="rounded-lg border border-blue-200 dark:border-blue-800/60 bg-blue-50/80 dark:bg-blue-950/30 px-4 py-3 text-sm text-blue-900 dark:text-blue-100">
        <p className="font-medium flex items-center gap-2">
          <Mail className="w-4 h-4 shrink-0" />
          After materializing
        </p>
        <p className="mt-1 text-blue-800/90 dark:text-blue-200/90">
          Review who will receive the send, import missing connections, add individuals, and purge
          scheduled rows before the send runs (when status is Ready to Publish).
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-center gap-2">
        <button
          type="button"
          onClick={onOpenSettings}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <Settings className="w-4 h-4" />
          Open Settings
        </button>
        <button
          type="button"
          onClick={onOpenContent}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <Mail className="w-4 h-4" />
          Open Content
        </button>
        <button
          type="button"
          onClick={onOpenActions}
          disabled={!settingsReady || !contentReady}
          title={
            !settingsReady || !contentReady
              ? 'Complete Settings and Content first'
              : 'Open Actions to Queue recipients or Send now'
          }
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Send className="w-4 h-4" />
          Open Actions
        </button>
      </div>

      {!hasSchedule ? (
        <p className="text-center text-xs text-gray-500 dark:text-gray-400 flex items-center justify-center gap-1.5">
          <Calendar className="w-3.5 h-3.5" />
          No send date yet — use Send now, or set a planned date in Settings before Queue
          recipients.
        </p>
      ) : null}
    </div>
  );
}
